import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import {constants} from "src/environments/constants";
import { ConfigService } from '../config/config.service';
import { UtilityService } from '../utility.service';
import { WizardStepName } from './wizard-step-name.enum';

@Injectable({
  providedIn: 'root'
})
export class WizardService {
  private readonly wizardStepsInOrder: WizardStep[] = [];

  private readonly _selectedWizardOptions = new BehaviorSubject<WizardOption[]>([]);
  readonly selectedWizardOptions$ = this._selectedWizardOptions.asObservable();

  private get selectedWizardOptions(): WizardOption[] {
    return this._selectedWizardOptions.getValue();
  }

  private set selectedWizardOptions(val: WizardOption[]) {
    this._selectedWizardOptions.next(val);
  }

  constructor(private confSvc: ConfigService, private utilityService: UtilityService) {
    this.wizardStepsInOrder = [
      new WizardStep(WizardStepName.PrimaryUse, this.confSvc.getString("primaryUseProgressText")),
      new WizardStep(WizardStepName.UseCase, this.confSvc.getString("useCaseProgressText")),
      new WizardStep(WizardStepName.NeedToRun, this.confSvc.getString("needToRunProgressText")),
      new WizardStep(WizardStepName.Brands, this.confSvc.getString("brandsProgressText")),
      new WizardStep(WizardStepName.Results, this.confSvc.getString("resultsProgressText"))
    ];
  }

  getStep(name: WizardStepName): WizardStep {
    return this.wizardStepsInOrder.find(step => step.name === name) ?? new WizardStep();
  }

  getStepNumber(name: WizardStepName): number {
    return this.wizardStepsInOrder.findIndex(step => step.name === name) + 1;
  }

  getTotalStepCount(): number {
    return this.wizardStepsInOrder.length;
  }

  addSelectedWizardOption(option: WizardOption): void {
    this.selectedWizardOptions = [
      ...this.selectedWizardOptions, option
    ];
  }

  removeSelectedWizardOption(title: string): void {
    this.selectedWizardOptions = this.selectedWizardOptions.filter(option => option.title !== title);
  }

  removeAllSelectedWizardOptions(): void {
    this.selectedWizardOptions = [];
  }

  getSelectedWizardOptionsForStep(stepName: WizardStepName): Observable<WizardOption[]> {
    return this.selectedWizardOptions$.pipe(
      take(1),
      map(options => options.filter(option => option.step.name === stepName))
    );
  }

  getSelectedWizardOptionsForStepAndWatch(stepName: WizardStepName): Observable<WizardOption[]> {
    return this.selectedWizardOptions$.pipe(
      map(options => options.filter(option => option.step.name === stepName))
    );
  }

  getStepRoutingQueryString(): Observable<string> {
    let queryString = `?${constants.LOCALE_QUERY_PARAM}=${this.utilityService.getLocaleInfo().localeName}`;

    return this.getSelectedWizardOptionsQueryString().pipe(
      map((wizardOptionsQueryString: string) => {
        if (wizardOptionsQueryString) {
          queryString += `&${wizardOptionsQueryString}`;
        }

        return queryString;
      })
    );
  }

  getSelectedWizardOptionsQueryString(): Observable<string> {
    const selectedPrimaryUseOptions$ = this.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse);
    const selectedNeedToRunOptions$ = this.getSelectedWizardOptionsForStep(WizardStepName.NeedToRun);
    const selectedUseCaseOptions$ = this.getSelectedWizardOptionsForStep(WizardStepName.UseCase);
    const selectedBrandOptions$ = this.getSelectedWizardOptionsForStep(WizardStepName.Brands);

    return forkJoin([selectedPrimaryUseOptions$, selectedNeedToRunOptions$, selectedUseCaseOptions$, selectedBrandOptions$])
      .pipe(
        map(([primaryUseOptions, needToRunOptions, useCaseOptions, brandOptions]) => {
          let queryString = "";

          queryString = this.appendWizardOptionsQueryParam(queryString, constants.PRIMARY_USE_QUERY_PARAM, primaryUseOptions);
          queryString = this.appendWizardOptionsQueryParam(queryString, constants.NEED_TO_RUN_QUERY_PARAM, needToRunOptions);
          queryString = this.appendWizardOptionsQueryParam(queryString, constants.USE_CASE_QUERY_PARAM, useCaseOptions);
          queryString = this.appendWizardOptionsQueryParam(queryString, constants.BRANDS_QUERY_PARAM, brandOptions);

          // remove the leading '&' so the caller can use this as a new query string if needed
          queryString = queryString ? queryString.substring(1) : queryString;

          return queryString;
        })
      );
  }

  appendWizardOptionsQueryParam(queryString: string, paramKey: string, options: WizardOption[]): string {
    if (options?.length) {
      queryString += `&${paramKey}=${this.getFormattedWizardOptionsForUri(options)}`;
    }

    return queryString;
  }

  getFormattedWizardOptionsForUri(options: WizardOption[]): string {
    return encodeURI(options.map(option => option.title).join()).replace(/&/g, "%26");
  }

  setSelectedOptionsFromUrlIfPresent(params: any): void {
    const urlHasParams = Object.keys(params).length > 0;

    if (urlHasParams) {
      this.removeAllSelectedWizardOptions();

      if (params[constants.PRIMARY_USE_QUERY_PARAM]) {
        this.addSelectedOptionsForParams(WizardStepName.PrimaryUse, params[constants.PRIMARY_USE_QUERY_PARAM]);
      }
      if (params[constants.NEED_TO_RUN_QUERY_PARAM]) {
        this.addSelectedOptionsForParams(WizardStepName.NeedToRun, params[constants.NEED_TO_RUN_QUERY_PARAM]);
      }
      if (params[constants.USE_CASE_QUERY_PARAM]) {
        this.addSelectedOptionsForParams(WizardStepName.UseCase, params[constants.USE_CASE_QUERY_PARAM]);
      }
      if (params[constants.BRANDS_QUERY_PARAM]) {
        this.addSelectedOptionsForParams(WizardStepName.Brands, params[constants.BRANDS_QUERY_PARAM]);
      }
    }
  }

  addSelectedOptionsForParams(stepName: WizardStepName, params: string): void {
    // this block is necessary because the link in the email for the "send me my results" feature contains some additional query params after ours,
    // and incorrectly uses ? rather than & when appending those params which causes their params to be read as part of ours.
    if (params.includes('?')) {
      params = params.split('?')[0];
    }

    params.split(',').forEach(selection => {
      const wizardOption = Object.assign(new WizardOption(), {
        step: new WizardStep(stepName),
        title: selection,
        subtitle: this.confSvc.getRelatedFieldFromConfig(selection, 'Title', 'Subtitle')
      });

      this.addSelectedWizardOption(wizardOption);
    });
  }

  /**
   * Checks to see if the optionTitle is in the array of optionsToDiplay
   * @params {string} optionTitle: title that currently being search
   * @params {string[]} optionsToDisplay string of options that are allowed to be displayed from the config
   * @returns boolean
   */
  shouldDisplayWizardOption(optionTitle: string, optionsToDisplay: string[]): boolean {
    return optionsToDisplay.some(option => option === optionTitle);
  }
}
